"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, Search } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  badge?: string;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  className?: string;
  /** Optional text rendered above the trigger as a native <label>. */
  label?: string;
}

export function CustomSelect({
  options,
  value,
  onChange,
  placeholder = "Select option...",
  searchable = false,
  className = "",
  label,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = options.filter(
    (o) =>
      !search.trim() ||
      o.label.toLowerCase().includes(search.toLowerCase()) ||
      o.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={className}>
      {label && (
        <label className="mb-1.5 block text-xs font-bold text-white/70">
          {label}
        </label>
      )}
      <div ref={containerRef} className="relative select-none">
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full bg-[#1C1C1E] border transition-all duration-200 rounded-xl px-4 py-3 flex items-center justify-between text-left ${
            isOpen
              ? "border-[#FFD60A] shadow-[0_0_0_3px_rgba(255,214,10,0.15)] bg-[#2C2C2E]"
              : "border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.25)]"
          }`}
        >
        <div className="flex flex-col truncate pr-2">
          {selectedOption ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#FAFAFA] truncate">
                {selectedOption.label}
              </span>
              {selectedOption.badge && (
                <span className="font-mono text-[10px] bg-white/10 text-[#FFD60A] px-1.5 py-0.5 rounded">
                  {selectedOption.badge}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-[#8E8E93]">{placeholder}</span>
          )}
          {selectedOption?.description && (
            <span className="text-xs text-[#8E8E93] truncate">
              {selectedOption.description}
            </span>
          )}
        </div>

        <ChevronDown
          className={`w-4 h-4 text-[#8E8E93] transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180 text-[#FFD60A]" : ""
          }`}
        />
      </button>

      {/* Menu Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.15 } }}
            transition={{ type: "spring", stiffness: 450, damping: 28 }}
            className="absolute z-50 left-0 right-0 mt-1 bg-[rgba(28,28,30,0.95)] backdrop-blur-2xl border border-[rgba(255,255,255,0.12)] rounded-xl shadow-[0_16px_48px_rgba(0,0,0,0.6)] p-2 max-h-64 overflow-y-auto"
          >
            {searchable && (
              <div className="sticky top-0 pb-2 mb-1 border-b border-white/10 bg-[rgba(28,28,30,0.95)] z-10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search options..."
                    className="w-full bg-[#141414] border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-xs text-[#FAFAFA] placeholder-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            <div className="space-y-1">
              {filteredOptions.length === 0 ? (
                <div className="p-3 text-center text-xs text-[#8E8E93]">
                  No matching options found.
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isActive = opt.value === value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => {
                        onChange(opt.value);
                        setIsOpen(false);
                      }}
                      className={`group relative cursor-pointer rounded-lg px-3.5 py-2.5 transition-all duration-150 flex items-center justify-between ${
                        isActive
                          ? "bg-[rgba(255,255,255,0.08)] text-[#FAFAFA]"
                          : "hover:bg-[rgba(255,255,255,0.05)] text-[#FAFAFA]/80 hover:text-[#FAFAFA]"
                      }`}
                    >
                      {/* Left Yellow Accent Bar */}
                      {isActive && (
                        <motion.div
                          layoutId="select-accent"
                          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] bg-[#FFD60A] rounded-r"
                        />
                      )}

                      <div className="flex flex-col min-w-0 pr-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isActive ? "font-bold text-white" : "font-medium"}`}>
                            {opt.label}
                          </span>
                          {opt.badge && (
                            <span className="font-mono text-[10px] bg-white/10 text-[#FFD60A] px-1.5 py-0.5 rounded">
                              {opt.badge}
                            </span>
                          )}
                        </div>
                        {opt.description && (
                          <span className="text-xs text-[#8E8E93] truncate">
                            {opt.description}
                          </span>
                        )}
                      </div>

                      {isActive && <Check className="w-4 h-4 text-[#FFD60A] shrink-0" />}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}
